import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AccountService } from '@frontendlib/common/services/account.service';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { InvitationAcceptStatus } from '@sharedlib/common/types/invitation-accept-status';
import type { EventParticipantDto, ReplyStatus } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { findMyParticipant, normalizeEmail, type RsvpTargetEvent } from '@modules/calendar-app/utils/event-rsvp.util';
import { type Observable, of, Subject } from 'rxjs';
import { finalize, map, take, takeUntil } from 'rxjs/operators';

/**
 * RSVP control (Yes / Maybe / No) for a calendar event the signed-in user was
 * invited to. Shared by the inbound-invitation email card and the event form so
 * both surfaces submit and reflect attendance identically.
 *
 * Renders only the buttons — the host supplies the surrounding "Are you
 * attending?" label so each surface keeps its native layout. The highlighted
 * button reflects THIS mailbox's participant entry (never an arbitrary
 * non-organizer on a group invite), and replies go through the existing
 * `EventsService.reply()` → `POST /api/calendar/events/:id/reply` path.
 */
@Component({
  selector: 'app-event-rsvp-buttons',
  templateUrl: './event-rsvp-buttons.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button2Component],
  host: { class: 'block' },
})
export class EventRsvpButtonsComponent {
  /** Event to RSVP to. Buttons stay un-highlighted until participants resolve. */
  public readonly event = input.required<RsvpTargetEvent | null>();
  public readonly replied = output<RsvpTargetEvent>();

  /**
   * Optional occurrence start (ISO 8601) to RSVP a single occurrence of a
   * recurring series; omit for a series-level reply. Used only when no
   * `scopeResolver` is provided.
   */
  public readonly occurrenceStart = input<string | undefined>(undefined);

  /**
   * Optional hook to resolve the reply scope at click time — e.g. ask
   * "This event / All events" for a recurring occurrence. Given the clicked
   * status, returns the `occurrenceStart` to reply with (ISO string = single
   * occurrence, `undefined` = whole series). Completing without emitting cancels
   * the reply. When unset, the static `occurrenceStart` input is used, which
   * preserves the email card's series-level behaviour.
   */
  public readonly scopeResolver = input<((status: ReplyStatus) => Observable<string | undefined>) | null>(null);

  private readonly eventsService = inject(EventsService);
  private readonly accountService = inject(AccountService);
  private readonly toasterService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  /** True while an RSVP request is in flight. */
  protected readonly loading = signal(false);

  /** Optimistic status override while a reply is in flight. */
  private readonly optimisticStatus = signal<InvitationAcceptStatus | null>(null);
  private readonly targetChanged = new Subject<void>();
  private targetKey: string | undefined;

  /**
   * Signed-in user's email (lower-cased, trimmed). Identifies the current user's
   * participant entry so the highlighted button reflects THIS mailbox.
   */
  private readonly currentUserEmail = toSignal(
    this.accountService.getAccount().pipe(map((account) => normalizeEmail(account.email))),
    { initialValue: null },
  );

  /**
   * The signed-in user's participant entry, or null when the user is the
   * organizer or not an attendee.
   */
  protected readonly currentParticipant = computed<EventParticipantDto | null>(() =>
    findMyParticipant(this.event(), this.currentUserEmail()),
  );

  protected readonly isAccepted = computed<boolean>(() => {
    const status = this.optimisticStatus() ?? this.currentParticipant()?.invitationAcceptStatus;
    return status === InvitationAcceptStatus.ACCEPTED;
  });

  protected readonly isTentative = computed<boolean>(() => {
    const status = this.optimisticStatus() ?? this.currentParticipant()?.invitationAcceptStatus;
    return status === InvitationAcceptStatus.TENTATIVE;
  });

  protected readonly isDeclined = computed<boolean>(() => {
    const status = this.optimisticStatus() ?? this.currentParticipant()?.invitationAcceptStatus;
    return status === InvitationAcceptStatus.REJECTED;
  });

  /** Expose enum for the template. */
  protected readonly InvitationAcceptStatus = InvitationAcceptStatus;

  constructor() {
    // A fresh event (e.g. an Event.UPDATED refetch) carries the server-confirmed
    // status, so drop any optimistic override and render server truth.
    effect(() => {
      const target = this.event();
      const key = target ? JSON.stringify([target.id, target.accountId, target.scopeKey]) : undefined;
      untracked(() => {
        if (this.targetKey !== key) {
          this.targetChanged.next();
          this.loading.set(false);
          this.targetKey = key;
        }
        this.optimisticStatus.set(null);
      });
    });
  }

  /**
   * Handle an RSVP button click. Resolves the recurrence scope first (a host
   * may pop a "This event / All events" dialog); a resolver that completes
   * without emitting cancels the reply.
   */
  protected onReply(status: ReplyStatus): void {
    const target = this.event();
    const id = target?.id;
    if (!target || !id || this.loading()) {
      return;
    }

    const resolver = this.scopeResolver();
    const occurrence$ = resolver ? resolver(status) : of(this.occurrenceStart());
    this.loading.set(true);
    let submitted = false;
    occurrence$
      .pipe(
        take(1),
        takeUntil(this.targetChanged),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (!submitted) this.loading.set(false);
        }),
      )
      .subscribe((occurrenceStart) => {
        if (!this.isCurrentTarget(target)) return;
        submitted = true;
        this.submitReply(target, status, occurrenceStart);
      });
  }

  private isCurrentTarget(target: RsvpTargetEvent): boolean {
    const current = this.event();
    return (
      current?.id === target.id && current?.accountId === target.accountId && current?.scopeKey === target.scopeKey
    );
  }

  /** Optimistic update + reply; roll back + toast on error. */
  private submitReply(target: RsvpTargetEvent, status: ReplyStatus, occurrenceStart: string | undefined): void {
    const id = target.id;
    if (!id || !this.isCurrentTarget(target)) return;

    const previousStatus = this.currentParticipant()?.invitationAcceptStatus ?? null;
    this.optimisticStatus.set(status);
    this.loading.set(true);

    // Pass occurrenceStart only when targeting a single occurrence; omit it for a
    // series-level reply so the call shape matches the original email-card path.
    const requestContext = owningTenantContext(target.accountId);
    const reply$ = requestContext
      ? this.eventsService.reply(id, status, occurrenceStart, { requestContext })
      : occurrenceStart === undefined
        ? this.eventsService.reply(id, status)
        : this.eventsService.reply(id, status, occurrenceStart);

    reply$.pipe(takeUntil(this.targetChanged), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (!this.isCurrentTarget(target)) return;
        this.loading.set(false);
        this.replied.emit(target);
      },
      error: () => {
        if (!this.isCurrentTarget(target)) return;
        this.optimisticStatus.set(previousStatus);
        this.loading.set(false);
        this.toasterService.show({ text: 'Failed to send RSVP. Please try again.' });
      },
    });
  }
}
